/**
 * Haptics Service
 * 
 * Servicio para feedback háptico (vibración táctil).
 * REQUERIDO para aprobación de Apple App Store (Directriz 4.2)
 * Mejora significativamente la experiencia de usuario nativa.
 * 
 * @module native/haptics
 */

import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { Logger } from '@/core/utils';

const logger = Logger.getLogger('HapticsService');

export type HapticImpact = 'light' | 'medium' | 'heavy';
export type HapticNotification = 'success' | 'warning' | 'error';

class HapticsService {
  private static instance: HapticsService;
  private isEnabled = true;

  private constructor() {}

  static getInstance(): HapticsService {
    if (!HapticsService.instance) {
      HapticsService.instance = new HapticsService();
    }
    return HapticsService.instance;
  }

  /**
   * Verifica si estamos en un dispositivo nativo
   */
  isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Habilita/deshabilita haptics (preferencia de usuario)
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    logger.info('Haptics', { enabled });
  }

  /**
   * Verifica si haptics está habilitado
   */
  isHapticsEnabled(): boolean {
    return this.isEnabled && this.isNativePlatform();
  }

  /**
   * Impacto háptico - para interacciones de UI
   */
  async impact(style: HapticImpact = 'medium'): Promise<void> {
    if (!this.isHapticsEnabled()) return;

    try {
      const impactStyle = this.getImpactStyle(style);
      await Haptics.impact({ style: impactStyle });
    } catch (error) {
      logger.error('Error en haptic impact', { error });
    }
  }

  /**
   * Notificación háptica - para feedback de acciones
   */
  async notification(type: HapticNotification = 'success'): Promise<void> {
    if (!this.isHapticsEnabled()) return;

    try {
      const notificationType = this.getNotificationType(type);
      await Haptics.notification({ type: notificationType });
    } catch (error) {
      logger.error('Error en haptic notification', { error });
    }
  }

  /**
   * Vibración simple
   */
  async vibrate(duration: number = 300): Promise<void> {
    if (!this.isHapticsEnabled()) return;

    try {
      await Haptics.vibrate({ duration });
    } catch (error) {
      logger.error('Error en vibración', { error });
    }
  }

  /**
   * Selección háptica - para selectores y pickers
   */
  async selectionStart(): Promise<void> {
    if (!this.isHapticsEnabled()) return;

    try {
      await Haptics.selectionStart();
    } catch (error) {
      logger.error('Error en selection start', { error });
    }
  }

  /**
   * Cambio de selección
   */
  async selectionChanged(): Promise<void> {
    if (!this.isHapticsEnabled()) return;

    try {
      await Haptics.selectionChanged();
    } catch (error) {
      logger.error('Error en selection changed', { error });
    }
  }

  /**
   * Fin de selección
   */
  async selectionEnd(): Promise<void> {
    if (!this.isHapticsEnabled()) return;

    try {
      await Haptics.selectionEnd();
    } catch (error) {
      logger.error('Error en selection end', { error });
    }
  }

  // ============================================
  // Métodos de alto nivel para casos de uso comunes
  // ============================================

  /**
   * Success haptic feedback (shorthand for notification('success'))
   */
  async success(): Promise<void> {
    await this.notification('success');
  }

  /**
   * Feedback al completar una práctica exitosamente
   */
  async practiceCompleted(): Promise<void> {
    await this.notification('success');
  }

  /**
   * Feedback al presionar un botón
   */
  async buttonTap(): Promise<void> {
    await this.impact('light');
  }

  /**
   * Feedback al cambiar de tab o página
   */
  async tabChange(): Promise<void> {
    await this.impact('medium');
  }

  /**
   * Feedback de error o acción no permitida
   */
  async errorFeedback(): Promise<void> {
    await this.notification('error');
  }

  /**
   * Feedback de advertencia
   */
  async warningFeedback(): Promise<void> {
    await this.notification('warning');
  }

  /**
   * Feedback al abrir modal o overlay
   */
  async modalOpen(): Promise<void> {
    await this.impact('medium');
  }

  /**
   * Feedback al cerrar modal
   */
  async modalClose(): Promise<void> {
    await this.impact('light');
  }

  /**
   * Feedback al hacer pull-to-refresh
   */
  async pullToRefresh(): Promise<void> {
    await this.impact('medium');
  }

  /**
   * Feedback al guardar datos
   */
  async saveSuccess(): Promise<void> {
    await this.notification('success');
  }

  /**
   * Feedback al eliminar elemento
   */
  async deleteAction(): Promise<void> {
    await this.notification('warning');
  }

  /**
   * Patrón de vibración personalizado (para casos especiales)
   */
  async customPattern(pattern: Array<{ type: 'vibrate' | 'pause'; duration: number }>): Promise<void> {
    if (!this.isHapticsEnabled()) return;

    for (const step of pattern) {
      if (step.type === 'vibrate') {
        await this.vibrate(step.duration);
      } else {
        await new Promise(resolve => setTimeout(resolve, step.duration));
      }
    }
  }

  /**
   * Patrón de celebración (completar objetivo importante)
   */
  async celebrationPattern(): Promise<void> {
    await this.customPattern([
      { type: 'vibrate', duration: 100 },
      { type: 'pause', duration: 100 },
      { type: 'vibrate', duration: 100 },
      { type: 'pause', duration: 100 },
      { type: 'vibrate', duration: 200 },
    ]);
  }

  // ============================================
  // Helpers privados
  // ============================================

  private getImpactStyle(style: HapticImpact): ImpactStyle {
    switch (style) {
      case 'light':
        return ImpactStyle.Light;
      case 'heavy':
        return ImpactStyle.Heavy;
      case 'medium':
      default:
        return ImpactStyle.Medium;
    }
  }

  private getNotificationType(type: HapticNotification): NotificationType {
    switch (type) {
      case 'warning':
        return NotificationType.Warning;
      case 'error':
        return NotificationType.Error;
      case 'success':
      default:
        return NotificationType.Success;
    }
  }
}

export const hapticsService = HapticsService.getInstance();










